import {
  App,
  AppDto,
  AppLimitReachedOrExceededError,
  CannotDeleteAppAsNonAdminError,
  CreateAppRequest,
  SCOPES,
  UpdateAppRequest,
  WebhookCreationFailedError,
  WebhookConflictError,
} from 'common/types/apps';
import config from 'config';
import api, { ApiError } from '../api';
import webhooks from './webhooks';

function getAvailableScopesForApp(app: AppDto): string[] {
  const scopes = [...SCOPES.map((s) => s.scope)];
  const visibleLegacyScopes = ['read:team', 'read:journal'];
  visibleLegacyScopes.forEach((legacyScope) => {
    if (app.scopes.includes(legacyScope)) scopes.push(legacyScope);
  });
  return scopes;
}

const mapDto = async (appDto: AppDto): Promise<App> => {
  const webhookUrls = await webhooks.getWebhooksForClientId(appDto.client_id);
  return {
    name: appDto.name,
    clientId: appDto.client_id,
    clientSecret: appDto.client_secret,
    contacts: appDto.contacts,
    logoUrl:
      appDto.logo_url ||
      'https://app.whoop.com/assets/images/avatar-default.png',
    privacyPolicyUrl: appDto.privacy_policy_url || undefined,
    redirectUrls: appDto.redirect_urls,
    scopes: appDto.scopes,
    availableScopes: getAvailableScopesForApp(appDto),
    dayRequestLimit: appDto.day_request_limit,
    minuteRequestLimit: appDto.minute_request_limit,
    // Preserve null: it means the app has no member limit, which is different
    // from the field being absent. `|| undefined` collapsed both (and 0).
    adoptedUserLimit: appDto.adopted_user_limit,
    adoptedUserCount: appDto.adopted_user_count,
    webhooks: webhookUrls.map(webhook => ({
      id: webhook.id,
      url: webhook.url,
      model_version: webhook.model_version || 'v1',
    })),
  };
};

const getApps = async (): Promise<App[]> => {
  const apps: AppDto[] = await api.get(`${config.apiBaseUrl}/v1/app`);
  return Promise.all(apps.map(mapDto));
};

const getApp = async (id: string): Promise<App> => {
  const appDto: AppDto = await api.get(`${config.apiBaseUrl}/v1/app/${id}`);
  return mapDto(appDto);
};

const createApp = async (createAppRequest: CreateAppRequest): Promise<App> => {
  let appDto: AppDto;
  try {
    appDto = await api.post(`${config.apiBaseUrl}/v1/app`, {
      name: createAppRequest.name,
      contacts: createAppRequest.contacts,
      logo_url: createAppRequest.logoUrl,
      privacy_policy_url: createAppRequest.privacyPolicyUrl,
      redirect_urls: createAppRequest.redirectUrls,
      scopes: createAppRequest.scopes,
    });
  } catch (e: any) {
    if (e instanceof ApiError && e.response.status === 400) {
      let body;
      try {
        body = await e.response.clone().text();
      } catch (_) {
        throw e;
      }
      if (body.includes('limit reached or exceeded')) {
        throw new AppLimitReachedOrExceededError();
      }
      throw e;
    }
  }

  // Map the DTO to our domain model so we can return or provide it in error cases
  const app = await mapDto(appDto!);

  // Attempt to create any requested webhooks
  if (createAppRequest.webhooks && createAppRequest.webhooks.length > 0) {
    try {
      await Promise.all(
        createAppRequest.webhooks.map((webhook) =>
          webhooks.createWebhookForClientId(
            appDto!.client_id,
            webhook.url,
            webhook.model_version,
          ),
        ),
      );
    } catch (err: any) {
      if (err instanceof ApiError && err.response.status === 409) {
        throw new WebhookConflictError(app);
      }
      // If webhook creation fails, surface a specific error including the new app
      throw new WebhookCreationFailedError(app);
    }
  }

  return app;
};

const updateApp = async (updateAppRequest: UpdateAppRequest): Promise<App> => {
  let appDto: AppDto;
  try {
    appDto = await api.put(
      `${config.apiBaseUrl}/v1/app/${updateAppRequest.clientId}`,
      {
        client_id: updateAppRequest.clientId,
        name: updateAppRequest.name,
        contacts: updateAppRequest.contacts,
        logo_url: updateAppRequest.logoUrl,
        privacy_policy_url: updateAppRequest.privacyPolicyUrl,
        redirect_urls: updateAppRequest.redirectUrls,
        scopes: updateAppRequest.scopes,
      },
    );
  } catch (e: any) {
    if (e instanceof ApiError && e.response.status === 400) {
      let body;
      try {
        body = await e.response.text();
        const parsed = JSON.parse(body);
        if (parsed && parsed.error_hint) {
          throw new Error(parsed.error_hint);
        }
      } catch (_) {
        /* ignore */
      }
    }
    throw e;
  }

  // Handle webhooks update
  if (updateAppRequest.webhooks) {
    // Get current webhooks to determine what to update/delete
    const currentWebhooks = await webhooks.getWebhooksForClientId(updateAppRequest.clientId);
    const currentWebhookIds = currentWebhooks.map(w => w.id);
    const requestWebhookIds = updateAppRequest.webhooks
      .filter(w => w.id)
      .map(w => w.id!);

    // Delete webhooks that are no longer in the request
    const webhooksToDelete = currentWebhookIds.filter(id => !requestWebhookIds.includes(id));
    // Use Promise.allSettled to ensure all deletion attempts are made
    const deletionResults = await Promise.allSettled(
      webhooksToDelete.map(id => webhooks.deleteWebhookById(id))
    );

    // Optional: Log any failed deletions for debugging
    deletionResults.forEach(result => {
      if (result.status === 'rejected') {
        // console.error('Failed to delete webhook:', result.reason);
      }
    });

    // Create new webhooks and update existing ones
    try {
      await Promise.all(
        updateAppRequest.webhooks.map((webhook) => {
          if (webhook.id) {
            // Update existing webhook
            return webhooks.updateWebhookByIdForClientId(
              webhook.id,
              updateAppRequest.clientId,
              webhook.url,
              webhook.model_version,
            );
          }
          // Create new webhook
          return webhooks.createWebhookForClientId(
            updateAppRequest.clientId,
            webhook.url,
            webhook.model_version,
          );
        }),
      );
    } catch (err: any) {
      if (err instanceof ApiError && err.response.status === 409) {
        throw new WebhookConflictError(await mapDto(appDto));
      }
      throw err;
    }
  }

  return mapDto(appDto);
};

const generateLogoPresignedUrl = (): Promise<string> =>
  api.getText(`${config.apiBaseUrl}/v1/app/logo/pre-signed`);

const uploadLogo = (f: File): Promise<string> =>
  api.postFile(`${config.apiBaseUrl}/v1/app/logo`, f);

const deleteApp = async (id: string): Promise<void> => {
  try {
    const app = await getApp(id);
    await Promise.all(
      app.webhooks.map((w) => webhooks.deleteWebhookById(w.id)),
    );
    await api.doDelete(`${config.apiBaseUrl}/v1/app/${id}`);
  } catch (e: any) {
    if (e instanceof ApiError && e.response.status === 403) {
      throw new CannotDeleteAppAsNonAdminError();
    }
    throw e;
  }
};

export default {
  getApps,
  getApp,
  createApp,
  updateApp,
  generateLogoPresignedUrl,
  uploadLogo,
  deleteApp,
};
